import React, { ReactNode, useCallback, useEffect, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import Button from '../../../../../components/envago/Button/Button';
import Icon from '../../../../../components/envago/Icon/Icon';
import { ImagePreview } from '../../../../../components/envago/ImagePreview';
import { readImageFileAsDataUri, SUPPORTED_READING_IMAGE_ACCEPT } from '../../../../../helper/ImageFileHelper';
import { RemoteResult, RemoteResultConfiguration } from '../../index.types';
import { useImageEvaluation } from '../../useImageEvaluation';
import { RemoteResultOutcomeGate } from '../RemoteResultOutcomeGate';
import { MAX_READING_IMAGES } from '../../../../../constants/ReadingImageLimits';
import { isMobilePlatform } from '../../../../../helper/PlatformHelper';
import { notificationActions } from '../../../../../redux/notification';

interface RemoteResultImageUploadProps {
    config: RemoteResultConfiguration;
    images: Array<string>;
    onRemoteResultReceived: (result: RemoteResult | undefined, imageBase64: string) => void;
    text?: string | ReactNode;
    primary?: boolean;
    onDelete?: (index: number) => void;
    deleteDisabled?: boolean;
}

const RemoteResultImageUpload = ({ config, images, onRemoteResultReceived, text = 'Ablesefoto hinzufügen', primary = false, onDelete, deleteDisabled }: RemoteResultImageUploadProps) => {
    const dispatch = useDispatch();
    const { t } = useTranslation();
    const inputRef = useRef<HTMLInputElement>(null);
    const isMountedRef = useRef(true);
    const { evaluate, loading: evaluating } = useImageEvaluation(config);

    useEffect(() => {
        // Set again on every mount: React 18 StrictMode runs mount → cleanup → mount in development.
        isMountedRef.current = true;

        return () => {
            isMountedRef.current = false;
        };
    }, []);

    const [isDragActive, setIsDragActive] = useState(false);
    const [pendingEvaluation, setPendingEvaluation] = useState<{ result: RemoteResult | undefined; imageBase64: string } | null>(null);
    const [loading, setLoading] = useState(false);

    const processFile = useCallback(
        async (file: File | undefined) => {
            if (!file) {
                return;
            }

            setLoading(true);

            try {
                let imageBase64: string;
                try {
                    imageBase64 = await readImageFileAsDataUri(file);
                } catch (err) {
                    console.log('Error in RemoteResultImageUpload: ', err);
                    dispatch(
                        notificationActions.showErrorNotification(
                            t('jobs.details.notifications.imageFormatUnsupported.title'),
                            t('jobs.details.notifications.imageFormatUnsupported.message'),
                        ),
                    );
                    return;
                }

                const { result, superseded } = await evaluate(imageBase64);

                // A newer upload has taken over (or the component is gone) — its result stands.
                if (superseded || !isMountedRef.current) {
                    return;
                }

                setPendingEvaluation({ result, imageBase64 });
            } finally {
                if (isMountedRef.current) {
                    setLoading(false);
                }
            }
        },
        [dispatch, evaluate, t],
    );

    const dragActive = !isMobilePlatform && isDragActive;
    const disabled = loading || evaluating || images.length >= MAX_READING_IMAGES;
    const iconPath = dragActive ? Icon.Path.mdiImageMove : Icon.Path.mdiCamera;

    return (
        <div className="upload-image">
            <Button
                primary={dragActive ? true : primary}
                ghost={!dragActive && !primary}
                disabled={disabled}
                isLoading={loading}
                aria={loading ? { 'aria-busy': true } : undefined}
                block={true}
                className={dragActive ? 'upload-image--drag-active border-dashed border-2 border-primary ring-2 ring-primary shadow-md' : undefined}
                onClick={() => {
                    inputRef.current?.click();
                }}
                onDragEnter={(event) => {
                    if (isMobilePlatform) return;

                    event.preventDefault();
                    setIsDragActive(true);
                }}
                onDragOver={(event) => {
                    if (isMobilePlatform) return;

                    event.preventDefault();
                    event.dataTransfer.dropEffect = 'copy';
                    setIsDragActive(true);
                }}
                onDragLeave={(event) => {
                    if (isMobilePlatform) return;

                    event.preventDefault();

                    if (event.currentTarget.contains(event.relatedTarget as Node)) {
                        return;
                    }

                    setIsDragActive(false);
                }}
                onDrop={async (event) => {
                    if (isMobilePlatform) return;

                    event.preventDefault();
                    event.stopPropagation();
                    setIsDragActive(false);
                    await processFile(event.dataTransfer.files?.[0]);
                }}
            >
                <Icon path={iconPath} /> {dragActive ? t('jobs.details.buttons.dropImage') : text}
            </Button>
            <input
                ref={inputRef}
                type="file"
                accept={SUPPORTED_READING_IMAGE_ACCEPT}
                hidden
                onChange={async (event) => {
                    const input = event.currentTarget;

                    try {
                        await processFile(input.files?.[0]);
                    } finally {
                        input.value = '';
                    }
                }}
            />
            {pendingEvaluation && (
                <RemoteResultOutcomeGate
                    config={config}
                    result={pendingEvaluation.result}
                    imageBase64={pendingEvaluation.imageBase64}
                    imageSource={'gallery'}
                    onCancel={() => {
                        setPendingEvaluation(null);
                    }}
                    onRetakeImage={() => {
                        setPendingEvaluation(null);
                        inputRef.current?.click();
                    }}
                    onConfirm={(result, imageBase64) => {
                        onRemoteResultReceived(result, imageBase64);
                        setPendingEvaluation(null);
                    }}
                />
            )}
            <ImagePreview data={images} onDelete={onDelete} disabled={deleteDisabled} />
        </div>
    );
};

export default RemoteResultImageUpload;
